import { Box } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { SectionHeading } from "./_lib/section-heading";
import { InvoiceCard } from "./invoice-card/invoice-card";
import {
  OutstandingBanner,
  useFormatOutstandingTotal,
} from "./outstanding-banner";
import type { V2InvoiceCardProps } from "./tab-content-types";

interface Props {
  invoices: V2InvoiceCardProps[];
}

const patientDueInPence = (invoice: V2InvoiceCardProps): number => {
  const patient = invoice.invoiceSummary.payers.find(
    (p) => p.type === "PATIENT",
  );
  return patient?.balance.due ?? 0;
};

export const OutstandingSection: FC<Props> = ({ invoices }) => {
  const formatTotal = useFormatOutstandingTotal();
  if (invoices.length === 0) return null;

  // Drafts have due === 0 until line items are added; we only surface the
  // banner when there's a real amount to act on, so a single empty draft
  // doesn't shout for attention.
  const totalDueInPence = invoices.reduce(
    (sum, invoice) => sum + patientDueInPence(invoice),
    0,
  );

  return (
    <Box data-testid="outstanding-section" mb={3}>
      {totalDueInPence > 0 && (
        <OutstandingBanner formattedAmount={formatTotal(totalDueInPence)} />
      )}
      <SectionHeading>
        <FormattedMessage
          id="invoicing.v2.section.outstanding"
          defaultMessage="Outstanding"
        />
      </SectionHeading>
      <Box display="flex" flexDirection="column">
        {invoices.map((invoice) => (
          <InvoiceCard key={invoice.invoiceId} {...invoice} />
        ))}
      </Box>
    </Box>
  );
};
